import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth, db, doc, onSnapshot } from '../firebase/config';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ---------- Auth listener ----------
  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
      } else {
        setUser(null);
        setUserData(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // ---------- Live userData subscription ----------
  useEffect(() => {
    if (!user) {
      setUserData(null);
      return;
    }

    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        if (snap.exists()) {
          setUserData({ uid: user.uid, ...snap.data() });
        } else {
          setUserData({ uid: user.uid, email: user.email });
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error subscribing to user data:', error);
        setUserData({ uid: user.uid, email: user.email });
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user]);

  const logout = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserData(null);
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      setUser(null);
      setUserData(null);
      throw error;
    }
  };

  const isProfessional = () => {
    return userData?.userType === 'professional' || userData?.role === 'professional';
  };

  const isClient = () => {
    return userData?.userType === 'client' || userData?.role === 'client';
  };

  const getDashboardRoute = () => {
    if (isProfessional()) return '/professional-dashboard';
    return '/dashboard';
  };

  const value = {
    user,
    userData,
    loading,
    setUserData,
    logout,
    isProfessional,
    isClient,
    getDashboardRoute
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};